import React, { KeyboardEvent as ReactKeyboardEvent } from "react";
import { FocusScope, useFocusManager } from "react-aria";

import { HeaderMenu, SimpleLinkBlock } from "@reactivated";

import { concatClassNames } from "@thelabnyc/thelabui/src/utils/styles";

import { Clickable } from "../Clickables";
import { Svg } from "../Svg";
import { NavPopover } from "./NavPopover";

import styles from "./main-menu-item.module.scss";

interface MainMenuItemProps {
    item:
        | { type: "link"; value: SimpleLinkBlock; id: string }
        | { type: "menu"; value: HeaderMenu; id: string };
    activeMenu: string | null;
    menuId: string;
    mobileMenuActive: boolean;
    hidden: boolean;
    setActiveMenu: (menuId: string | null) => void;
}

export const MainMenuItem = (props: MainMenuItemProps) => {
    const focusManager = useFocusManager();
    const onKeyDown = (event: ReactKeyboardEvent<HTMLButtonElement>) => {
        if (!focusManager || props.activeMenu !== null) return;
        switch (event.key) {
            case "ArrowRight":
                focusManager.focusNext({ wrap: true });
                break;
            case "ArrowLeft":
                focusManager.focusPrevious({ wrap: true });
                break;
            case "End":
                event.preventDefault();
                focusManager.focusLast();
                break;
            case "Home":
                event.preventDefault();
                focusManager.focusFirst();
                break;
        }
    };

    const handleMouseEnter = () => {
        if (!props.mobileMenuActive && props.item.type === "menu") {
            props.setActiveMenu(props.menuId);
        }
    };

    const handleMouseLeave = (
        event: React.MouseEvent<HTMLLIElement, MouseEvent>,
    ) => {
        const relatedTarget = event.relatedTarget;
        if (!(relatedTarget instanceof HTMLElement)) return;
        // Check if we're NOT moving to another li
        if (!relatedTarget.closest("li")) {
            if (!props.mobileMenuActive && props.item.type === "menu") {
                props.setActiveMenu(null);
            }
        }
    };

    const isMenu = props.item.type === "menu";
    const title =
        props.item.type === "menu"
            ? props.item.value.title
            : props.item.value.display_name;
    const url = props.item.type === "link" ? props.item.value.url : undefined;
    const expanded = props.activeMenu === props.menuId;

    return (
        <li
            className={props.hidden ? styles.l1liHidden : ""}
            onMouseEnter={handleMouseEnter}
            onMouseLeave={handleMouseLeave}
        >
            {isMenu ? (
                <>
                    <Clickable
                        className={concatClassNames([
                            props.mobileMenuActive
                                ? styles.l1NavLinkMobile
                                : styles.l1NavLink,
                            !props.hidden && expanded
                                ? styles.l1NavLinkActive
                                : "",
                        ])}
                        aria-expanded={expanded}
                        aria-controls={props.menuId}
                        onPress={() => props.setActiveMenu(props.menuId)}
                        onKeyDown={onKeyDown}
                    >
                        <span>{title}</span>

                        {props.mobileMenuActive && (
                            <span className={styles.l1NavLinkAction}>
                                <Svg
                                    className={styles.caretRight}
                                    name="caret"
                                    visuallyHiddenText={null}
                                    aria-hidden
                                />
                                <span>Back</span>
                            </span>
                        )}
                    </Clickable>
                    {expanded && props.item.type === "menu" && (
                        <FocusScope contain={false} restoreFocus autoFocus>
                            <NavPopover
                                menu={props.item.value}
                                menuId={props.menuId}
                                mobileDesign={props.mobileMenuActive}
                                setActiveMenu={props.setActiveMenu}
                            />
                        </FocusScope>
                    )}
                </>
            ) : (
                <Clickable
                    href={url ?? undefined}
                    className={
                        props.mobileMenuActive
                            ? styles.l1NavLinkMobile
                            : styles.l1NavLink
                    }
                    onKeyDown={onKeyDown}
                >
                    {title}
                </Clickable>
            )}
        </li>
    );
};
